import React, { useState } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { Activity, Boxes, ChevronRight, LibraryBig, LifeBuoy, PanelsTopLeft, Settings } from "lucide-react";
import type { MenuItem, SiteData } from "../shared/types";
import { siteUrl } from "../shared/paths";
import { MethodBadge } from "../components/MethodBadge";
import { COPY } from "../copy";

/**
 * The left menu of one tab (design §0.2): Status · Support · Panel at the top, groups with icons
 * (Documentation only), sub-groups fold and the branch of the open page is open, at most three
 * levels, method labels on operation pages.
 */

const GROUP_ICONS: Record<string, React.ReactNode> = {
  settings: <Settings size={14} />,
  boxes: <Boxes size={14} />,
  library: <LibraryBig size={14} />,
};

const containsPath = (item: MenuItem, path: string): boolean => item.path === path || Boolean(item.children?.some((c) => containsPath(c, path)));

const Item = ({ item, path, depth, onNavigate }: { item: MenuItem; path: string; depth: number; onNavigate: () => void }) => {
  const [open, setOpen] = useState(() => containsPath(item, path));
  if (item.children) {
    const top = depth === 0;

    return (
      <li className={top ? "mt-5" : ""}>
        {top ? (
          <p className="mb-1 flex items-center gap-2 px-2 text-headline1 uppercase text-muted-foreground">
            {item.icon ? GROUP_ICONS[item.icon] : null}
            {item.label}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-bodySmall text-foreground hover:bg-muted"
            aria-expanded={open}
          >
            {item.label}
            <ChevronRight size={14} className={clsx("transition-transform", open && "rotate-90")} />
          </button>
        )}
        <ul className={clsx(!top && "ml-2 border-l border-border pl-2")} hidden={!top && !open}>
          {item.children.map((child) => (
            <Item key={`${child.label}-${child.path ?? ""}`} item={child} path={path} depth={depth + 1} onNavigate={onNavigate} />
          ))}
        </ul>
      </li>
    );
  }
  const active = item.path === path;

  return (
    <li>
      <Link
        to={siteUrl(item.path ?? "/")}
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        className={clsx("flex items-center gap-2 rounded px-2 py-1.5 text-bodySmall", active ? "bg-muted font-semibold text-foreground" : "text-muted-foreground hover:text-foreground")}
      >
        {item.method ? <MethodBadge method={item.method} /> : null}
        <span className="min-w-0 truncate">{item.label}</span>
      </Link>
    </li>
  );
};

export const Sidebar = ({ site, tab, path, onNavigate }: { site: SiteData; tab: string; path: string; onNavigate: () => void }) => (
  <nav aria-label={COPY.navigation} className="text-bodySmall">
    <ul className="mb-2 space-y-1 border-b border-border pb-3">
      {site.links.status ? (
        <li>
          <a href={site.links.status} className="flex items-center gap-2 px-2 py-1 text-muted-foreground hover:text-foreground">
            <Activity size={14} />
            {COPY.status}
          </a>
        </li>
      ) : null}
      <li>
        <Link to={siteUrl(site.links.support)} onClick={onNavigate} className="flex items-center gap-2 px-2 py-1 text-muted-foreground hover:text-foreground">
          <LifeBuoy size={14} />
          {COPY.support}
        </Link>
      </li>
      <li>
        <a href={site.links.panel} className="flex items-center gap-2 px-2 py-1 text-muted-foreground hover:text-foreground">
          <PanelsTopLeft size={14} />
          {COPY.panel}
        </a>
      </li>
    </ul>
    <ul>
      {(site.menus[tab] ?? []).map((item) => (
        <Item key={`${item.label}-${item.path ?? ""}-${path}`} item={item} path={path} depth={item.children ? 0 : 1} onNavigate={onNavigate} />
      ))}
    </ul>
  </nav>
);
